import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { Loader2, Shield, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface PlatformAdminRouteProps {
  children: React.ReactNode;
}

export function PlatformAdminRoute({ children }: PlatformAdminRouteProps) {
  const { user, isPlatformAdmin, validationState, loading, refreshValidation } = usePlatformAdmin();
  const navigate = useNavigate();
  const { t } = useTranslation(['platformAdmin', 'auth']);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/secure-admin-portal');
        return;
      }

      // Only redirect to login if explicitly invalid (not unknown)
      if (validationState === 'invalid') {
        navigate('/secure-admin-portal');
        return;
      }
    }
  }, [user, validationState, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('platformAdmin:accessGuard.verifying')}</p>
        </div>
      </div>
    );
  }

  // Show retry screen for unknown validation state
  if (validationState === 'unknown') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">{t('platformAdmin:accessGuard.connectionIssue')}</h2>
          <p className="text-muted-foreground mb-6">
            {t('platformAdmin:accessGuard.connectionIssueMessage')}
          </p>
          <Button onClick={refreshValidation} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('auth:retryConnection')}
          </Button>
        </div>
      </div>
    );
  }

  if (!user || validationState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">{t('platformAdmin:accessGuard.accessRequired')}</h2>
          <p className="text-muted-foreground">{t('platformAdmin:accessGuard.redirecting')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}